import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, Users, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';
import RelatedServices from './RelatedServices';

const FinancialAdvisorIndia = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.financialAdvisorIndia} faqJsonLd={faqSchemas.financialAdvisorIndia} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  SEBI REGISTERED FINANCIAL ADVISOR
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Trusted Financial Advisor in
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  India Since 1996
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert investment guidance for Indians and NRIs. As a SEBI-registered financial advisor India, 
                we help you build lasting wealth through systematic planning, diversified portfolios, and professional 
                wealth management services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  View Services
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/7993568/pexels-photo-7993568.jpeg?w=800&q=85" 
                  alt="Best financial advisor in India - SEBI registered investment advisor providing expert portfolio management and wealth creation services since 1996"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                25+ Years
              </div>
              <div className="text-gray-600">Experience in Financial Advisory</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                ₹500 Cr+
              </div>
              <div className="text-gray-600">Assets Under Management</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                5000+
              </div>
              <div className="text-gray-600">Satisfied Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                SEBI
              </div>
              <div className="text-gray-600">Registered & Licensed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Rumtek as Your Financial Advisor in India?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Combining decades of experience with personalized service to help you achieve your financial goals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">SEBI Registered</h3>
              <p className="text-gray-600 leading-relaxed">
                Fully licensed and regulated financial advisor by SEBI. Member of NSE, BSE, and MCX through 
                Sunidhi Securities, ensuring your investments are in safe, professional hands.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                Since 1996, we've helped 5000+ clients build wealth systematically. Our experience spans 
                multiple market cycles, economic changes, and regulatory updates.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Service</h3>
              <p className="text-gray-600 leading-relaxed">
                Every client receives customized investment strategies based on their unique goals, risk tolerance, 
                and financial situation. No cookie-cutter solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Financial Advisory Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As your trusted financial advisor in India, we offer end-to-end investment solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Equity & Stock Market",
                description: "Direct equity investments in fundamentally strong companies with research-backed recommendations",
                minInvestment: "₹1 Lakh"
              },
              {
                title: "Mutual Funds",
                description: "Diversified mutual fund portfolios across all major AMCs with systematic investment planning",
                minInvestment: "₹5,000/month"
              },
              {
                title: "Portfolio Management",
                description: "Professional PMS services with customized strategies and dedicated relationship managers",
                minInvestment: "₹50 Lakhs"
              },
              {
                title: "Bonds & Fixed Income",
                description: "GOI, corporate, and state bonds with A+ ratings for stable, predictable returns",
                minInvestment: "₹5 Lakhs"
              },
              {
                title: "Alternative Investments",
                description: "AIF solutions including debt and multi-asset funds for portfolio diversification",
                minInvestment: "₹1 Crore"
              },
              {
                title: "Retirement Planning",
                description: "Goal-based retirement corpus building through systematic investments and asset allocation",
                minInvestment: "As per goals"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-sm font-semibold text-blue-600">Min Investment: {service.minInvestment}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Financial Advisory Process
            </h2>
            <p className="text-xl text-gray-600">
              A systematic, transparent approach to wealth creation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Understanding Goals",
                description: "We start by understanding your financial goals, risk tolerance, and investment timeline"
              },
              {
                step: "02",
                title: "Strategy Development",
                description: "Create a personalized investment strategy with optimal asset allocation"
              },
              {
                step: "03",
                title: "Implementation",
                description: "Execute the strategy with best-in-class investment products and regular monitoring"
              },
              {
                step: "04",
                title: "Review & Rebalance",
                description: "Quarterly reviews and portfolio rebalancing to stay on track with your goals"
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Real success stories from real people
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Rajesh Kumar",
                location: "Mumbai",
                testimonial: "Best financial advisor in India! Started with ₹25,000 monthly SIP 8 years ago. Today my portfolio is worth ₹1.2 crores. Their systematic approach really works."
              },
              {
                name: "Priya Sharma",
                location: "Bangalore",
                testimonial: "Their retirement planning helped me retire at 55 with a comfortable corpus. Expert guidance made all the difference in achieving financial freedom."
              },
              {
                name: "Anil Mehta",
                location: "Kolkata",
                testimonial: "PMS service has given me 16% annual returns consistently. Their research and stock selection is outstanding. Highly recommend as financial advisor India."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.testimonial}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about financial advisory services in India
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "How do I choose a financial advisor in India?",
                answer: "Look for SEBI registration, experience (10+ years), transparent fee structure, personalized service, and proven track record. Check credentials through SEBI website. At Rumtek, we've been serving clients since 1996 with complete transparency."
              },
              {
                question: "What are the fees for financial advisory services?",
                answer: "Fees vary by service: equity trading charges brokerage, mutual funds earn AMC commissions (no extra cost to you), PMS/AIF charge percentage of AUM. We provide complete fee disclosure upfront with no hidden charges."
              },
              {
                question: "Is it worth hiring a financial advisor in India?",
                answer: "Yes! Professional advisors help with asset allocation, tax optimization, goal-based planning, and avoiding emotional decisions. Our clients have consistently outperformed DIY investors through systematic strategies."
              },
              {
                question: "Can financial advisors help with tax planning?",
                answer: "Absolutely! We help with tax-efficient investing through ELSS, NPS, PPF recommendations, capital gains management, and overall tax optimization strategies within your investment plan."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['mutualFund', 'nri', 'retirement', 'wealth', 'equity', 'pms']} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Wealth Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Get expert financial advice from India's trusted advisors. Book your free consultation today 
            and discover how systematic investing can transform your financial future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/calculator"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Calculate Your Returns
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialAdvisorIndia;
